import {
  ColumnData,
  SlideAction,
  SlideLayout,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NewAlertHeaderAction } from '../../shared/components/alerts/newAlertHeaderAction';
import { AssetRouteProvider } from '../assetRouteContext';
import { isActivityOrEditPage } from '../assetUtils';
import { AlertPanel } from './alert-panel';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';

export const AssetRoutineAlerts = () => {
  const { assetId, routineId, issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const { t } = useTranslation();
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!assetId || !routineId) {
    return null;
  }

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <AssetRoutineSubmitColumn
        assetId={assetId}
        backGuardRef={backGuardRef}
      />
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => navigate('../../..', { relative: 'path' }),
  };

  const rightColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: (
      <AlertPanel
        assetId={assetId}
        routineId={routineId}
      />
    ),
    headerComponent: (
      <NewAlertHeaderAction
        accessibilityLabel={t('alerts.setAlert')}
        onBeforeNavigate={async () => {
          if (slideLayout && !isActivityOrEditPage(location)) {
            await slideLayout.onSlideChange(SlideAction.NEXT);
          }
        }}
      />
    ),
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate('..', { relative: 'path' }),
    showBackButton: true,
    showHeader: true,
    disableScroll: true,
  };

  return (
    <AssetRouteProvider value={{
      basePath: issueId ? `/activities/${issueId}/asset/${assetId}` : `/assets/${assetId}`,
      listPath: issueId ? '/activities' : '/assets',
      isWorkspaceMode: false,
      issueId: issueId ?? undefined,
    }}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
      />
    </AssetRouteProvider>
  );
};
